import { useRef, useState } from "react";
import { Stack, TextField } from "@mui/material";
import { useSnackbar } from "notistack";

import Sheet, { SheetContent, SheetHeader, SheetSaveBar } from "../components/Sheet";
import { addLinkToRecord, linkRefusal } from "./addLink";
import type { RecordFile } from "./types";

interface AddLinkSheetProps {
  open: boolean;
  onClose: () => void;
  /** The Files tab's `source` (resource_files.source_type) and record id. */
  source: string;
  id: number;
  onAdded: (file: RecordFile) => void;
}

/** Files tab → Add link: a URL kept on the record like a file (attachments spec §5.3).
 *  A record's action form, so the right-hand Sheet with its sticky footer (DESIGN-PATTERNS;
 *  the footer is SheetSaveBar, as in Blank's create sheet). The server decides what a valid
 *  link is (http/https, a host, no user:password) and words the refusal; this shows its
 *  words. All it checks itself is that there is something to send. */
export default function AddLinkSheet({ open, onClose, source, id, onAdded }: AddLinkSheetProps) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const { enqueueSnackbar } = useSnackbar();

  const close = () => {
    setUrl("");
    setTitle("");
    onClose();
  };

  const save = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    try {
      const file = await addLinkToRecord({ url, title, source, id });
      onAdded(file);
      close();
    } catch (e) {
      enqueueSnackbar(linkRefusal(e), { variant: "error" });
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onClose={close} dialogLabel="Add link" data-testid="add-link-sheet">
      <SheetHeader title="Add link" onClose={close} />
      <SheetContent>
        {/* Enter submits, as in Blank's create sheet: the form owns the submit, SheetSaveBar's
            primary is its submit button. */}
        <form
          noValidate
          data-testid="add-link-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (url.trim()) void save();
          }}
        >
          <Stack spacing={2}>
            <TextField
              label="Link"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://"
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              helperText="Optional. Shown in the Files tab instead of the address."
              inputProps={{ maxLength: 100 }}
              fullWidth
            />
          </Stack>
          <SheetSaveBar
            onCancel={close}
            primaryLabel="Add link"
            submit
            disabled={!url.trim() || saving}
          />
        </form>
      </SheetContent>
    </Sheet>
  );
}
